/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module IpamModule
 */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import { CustomIpamDnsCollection }
    from 'ajs/modules/ipam/factories/custom-ipam-dns-profile.collection.factory';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import * as globalL10n from 'global-l10n';

import * as l10n from './custom-ipam.l10n';

const { ...globalL10nKeys } = globalL10n;

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TCustomIpamDnsCollection = typeof CustomIpamDnsCollection;

/**
 * @description Custom ipam dns page.
 *
 * @author Anjali Tanpure
 */
@Component({
    selector: 'custom-ipam-dns-profile-list-page',
    templateUrl: './custom-ipam-dns-profile-list-page.component.html',
})
export class CustomIpamDnsProfileListPageComponent implements OnInit, OnDestroy {
    /**
     * Configuration object to display custom ipam dns collection.
     */
    public customIpamDnsGridConfig: IAviCollectionDataGridConfig;

    /**
     * Keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Global Keys from source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Collection object to store the custom ipam dns collection.
     */
    private readonly customIpamDnsCollection: CustomIpamDnsCollection;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(CustomIpamDnsCollection)
        CustomIpamDnsCollection: TCustomIpamDnsCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.customIpamDnsCollection = new CustomIpamDnsCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.customIpamDnsCollection;

        this.customIpamDnsGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.customIpamDnsCollection,
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleName),
                    transform: (row: IAviCollectionDataGridRow) => row.getName(),
                },
                {
                    id: 'script_uri',
                    label: this.l10nService.getMessage(l10nKeys.columnTitleScript),
                    transform: (row: IAviCollectionDataGridRow) => row.getConfig().script_uri,
                },
            ],
            layout: {
                placeholderMessage: this.l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.customIpamDnsCollection.destroy();
    }
}
